import type { PatSnapSuccessResponseData } from '@patsnap/tahiti-core';
import { PsHttpService } from '@patsnap/tahiti-core';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type {
    GetReportParagraphsApi,
    GetReportParagraphsRequest,
    GetReportParagraphsResponse,
} from '../report-paragraphs-api';
import { coreTelecomApiUrlPrefix } from '@telecom/shared/domain-config';
import type { StartCheckContentApi, StartCheckContentRequest } from '../start-check-content-api';
import type { GetCheckContentApi, GetCheckContentRequest, GetCheckContentResponse } from '../get-check-content-api';
import type { BatchIgnoreApi, BatchIgnoreRequest, BatchIgnoreResponse } from '../batch-ignore-api';
import { FactoryPsHttpSSE } from '@/services/sse-ps-http/sse-ps-http';
import type { FixContentApi, ApiContentResponse, FixContentRequest, FixContentResponse } from '../fix-content-api';
import type { UpdateContentApi, UpdateContentRequest, UpdateContentResponse } from '../update-content-api';
import type { CheckStatisticsApi, CheckStatisticsParams, CheckStatisticsResponse } from '../check-statistics-api';

@injectable()
export class ReportParagraphsApiImpl extends PsHttpService
    implements
    GetReportParagraphsApi,
    StartCheckContentApi,
    BatchIgnoreApi,
    FixContentApi,
    UpdateContentApi,
    CheckStatisticsApi,
    GetCheckContentApi
{
    get options() {
        return {
            baseURL: coreTelecomApiUrlPrefix,
            headers: {
                'X-API-Version': '2.0',
            },
        };
    }

    async getReportParagraphs(data: GetReportParagraphsRequest, abortSignal?: AbortSignal): Promise<GetReportParagraphsResponse> {
        const result = await this.get<PatSnapSuccessResponseData<GetReportParagraphsResponse>>({
            url: `/ai-specification/task/${data.task_id}/report-paragraphs`,
            signal: abortSignal,
        });
        return result.data;
    }

    async startCheckContent(data: StartCheckContentRequest, abortSignal?: AbortSignal): Promise<void> {
        const result = await this.post<PatSnapSuccessResponseData<void>>({
            url: '/ai-specification/task/start-check-content',
            data,
            signal: abortSignal,
        });
        return result.data;
    }

    async getCheckContent(data: GetCheckContentRequest, abortSignal?: AbortSignal): Promise<GetCheckContentResponse> {
        const result = await this.post<PatSnapSuccessResponseData<GetCheckContentResponse>>({
            url: '/ai-check-result/get-check-content',
            data,
            signal: abortSignal,
        });
        return result.data;
    }

    async batchIgnore(data: BatchIgnoreRequest): Promise<BatchIgnoreResponse> {
        const result = await this.put<PatSnapSuccessResponseData<BatchIgnoreResponse>>({
            url: '/ai-check-result/batch-ignore',
            data,
        });
        return result.data;
    }

    @inject(FactoryPsHttpSSE)
    private _factorySsePsHttp!: FactoryPsHttpSSE;

    fixContent(data: FixContentRequest): FixContentResponse {
        const sse = this._factorySsePsHttp<ApiContentResponse>().init({
            url: '/ai-specification/task/fix-content',
            data,
        });
        return sse;
    }

    async updateContent(data: UpdateContentRequest): Promise<UpdateContentResponse> {
        const result = await this.put<PatSnapSuccessResponseData<UpdateContentResponse>>({
            url: '/ai-specification/task/update-content',
            data,
        });
        return result.data;
    }

    async checkStatistics(params: CheckStatisticsParams): Promise<CheckStatisticsResponse> {
        const result = await this.get<PatSnapSuccessResponseData<CheckStatisticsResponse>>({
            url: `/ai-specification/task/${params.taskId}/check-statistics`,
        });
        return result.data;
    }
}
